import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import { packsAPI } from '../../services/api';
import ConnectionPanel from './ConnectionPanel';
import AgentPanel from './AgentPanel';
import { FRESHNESS_LABEL, datasetPath, formatDateTime, formatNumber, targetValue } from './dataSourceFormat';
import PackLogo from './PackLogo';
import '../../styles/DatasetManagement.css';
import '../../styles/DataSources.css';

const CADENCE_KPI = { day: 'Daily', week: 'Weekly', month: 'Monthly', quarter: 'Quarterly', year: 'Yearly' };
const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="pack-copy-field">
      <span className="pack-copy-label">{label}</span>
      <code>{value}</code>
      <button type="button" className="btn-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

function DatasetDefinition({ dataset }) {
  const state = dataset.dataset;
  return (
    <details className="pack-dataset">
      <summary>
        <span className="pack-dataset-title">
          <strong>{dataset.title}</strong> <code>{dataset.name}</code>
        </span>
        <span className="pack-dataset-meta">
          {dataset.columns.length} columns · {dataset.measures.length} measures
          {state.exists && (
            <> · {formatNumber(state.rowCount)} rows <span className={`dataset-badge freshness-${state.freshness}`}>{FRESHNESS_LABEL[state.freshness] || state.freshness}</span></>
          )}
        </span>
      </summary>
      <p className="source-hint">{dataset.description}</p>
      {state.exists && <p className="source-hint"><Link to={datasetPath(dataset.name)}>Open the {dataset.name} dataset</Link> · last synced {formatDateTime(state.lastSyncedAt)}</p>}

      <h4>Columns</h4>
      <table className="datasets-table pack-columns">
        <thead><tr><th>Column</th><th>Type</th><th>Description</th></tr></thead>
        <tbody>
          {dataset.columns.map((c) => (
            <tr key={c.name}>
              <td><code>{c.name}</code>{c.name === dataset.primaryKey && <span className="dataset-badge info">key</span>}{c.required && c.name !== dataset.primaryKey && <span className="dataset-badge">required</span>}</td>
              <td>{c.type}</td>
              <td>{c.description || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {dataset.relationships.length > 0 && (
        <p className="source-hint">Joins: {dataset.relationships.map((r) => <span key={r.column}><code>{r.column}</code> → <code>{r.references}</code> </span>)}</p>
      )}

      <h4>Measures</h4>
      <ul className="pack-list">
        {dataset.measures.map((m) => (
          <li key={m.name}><strong>{m.title}</strong>{m.description ? <> · {m.description}</> : null}</li>
        ))}
      </ul>
      {dataset.reports.length > 0 && (
        <>
          <h4>Ready-made reports</h4>
          <ul className="pack-list">{dataset.reports.map((r) => <li key={r.key}>{r.title}</li>)}</ul>
        </>
      )}
    </details>
  );
}

/**
 * One data source pack: the datasets it defines, their measures and starter
 * reports, and the KPIs it suggests. Admins install it here, choosing KPIs and
 * their targets; installing returns the API key Kinspeed Connect pushes with.
 */
const PackPage = () => {
  const { id } = useParams();
  const companyId = useStore((state) => state.currentCompany?.id);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [state, setState] = useState({ loading: true, error: '', pack: null });
  const [selected, setSelected] = useState({});
  const [targets, setTargets] = useState({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [result, setResult] = useState(null);
  const [deleteData, setDeleteData] = useState(false);
  const [updateKpis, setUpdateKpis] = useState({});
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setState((s) => ({ ...s, loading: true, error: '' }));
      const res = await packsAPI.get(id, companyId);
      const pack = res?.success ? res.data : null;
      setState({ loading: false, error: '', pack });
      if (pack && !pack.installed) {
        setSelected(Object.fromEntries(pack.datasets.flatMap((d) => d.kpis.map((k) => [k.key, !k.alreadyDefined]))));
      }
    } catch (err) {
      setState({ loading: false, error: describeError(err, 'Failed to load the data source'), pack: null });
    }
  }, [id, companyId]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(
    () => (state.pack ? state.pack.datasets.flatMap((d) => d.kpis.map((k) => ({ ...k, dataset: d.name }))) : []),
    [state.pack]
  );

  const install = async () => {
    const chosen = kpis.filter((k) => selected[k.key]);
    const bad = chosen.find((k) => Number.isNaN(targetValue(k, targets[k.key])));
    if (bad) {
      setActionError(`The target for ${bad.name} must be a number.`);
      return;
    }
    try {
      setBusy(true);
      setActionError('');
      const res = await packsAPI.install(id, companyId, {
        kpis: chosen.map((k) => ({ key: k.key, target: targetValue(k, targets[k.key]) })),
      });
      setResult(res.data);
      await load();
    } catch (err) {
      setActionError(describeError(err, 'Install failed'));
    } finally {
      setBusy(false);
    }
  };

  const applyUpdate = async () => {
    const offered = state.pack.update?.kpis || [];
    try {
      setBusy(true);
      setActionError('');
      const res = await packsAPI.update(id, companyId, { kpis: offered.filter((k) => updateKpis[k.key] !== false).map((k) => ({ key: k.key })) });
      const added = res.data.datasets.filter((d) => d.created).length;
      setNotice(`${state.pack.title} updated. ${added} ${added === 1 ? 'dataset' : 'datasets'} and ${res.data.kpis.length} ${res.data.kpis.length === 1 ? 'KPI' : 'KPIs'} added.`);
      await load();
    } catch (err) {
      setActionError(describeError(err, 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  const uninstall = async () => {
    try {
      setBusy(true);
      setActionError('');
      const res = await packsAPI.uninstall(id, companyId, deleteData);
      setResult(null);
      setNotice(res.data.deletedDatasets.length
        ? `${state.pack.title} uninstalled; ${res.data.deletedDatasets.length} datasets and their KPIs deleted.`
        : `${state.pack.title} uninstalled. Its datasets and KPIs were kept.`);
      setDeleteData(false);
      await load();
    } catch (err) {
      setActionError(describeError(err, 'Uninstall failed'));
    } finally {
      setBusy(false);
    }
  };

  const { pack } = state;
  if (state.loading && !pack) return <div className="dataset-management"><div className="loading-spinner" /></div>;

  return (
    <div className="dataset-management data-source-detail">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/data-sources">Data sources</Link> / <Link to="/admin/data-sources/packs">Packs</Link> / {pack?.title || id}
      </nav>
      {state.error && <div className="error-message" role="alert">{state.error}</div>}
      {pack && (
        <>
          <div className="page-header">
            <div className="pack-page-title">
              <PackLogo id={pack.id} title={pack.title} size="large" />
              <div>
                <h2>{pack.title}</h2>
                <p className="page-subtitle">{pack.description}</p>
              </div>
            </div>
            <div className="page-actions">
              {pack.installed
                ? <span className="dataset-badge ok">Installed</span>
                : <span className="dataset-badge">Not installed</span>}
            </div>
          </div>

          {actionError && <div className="error-message" role="alert">{actionError}</div>}
          {notice && <div className="success-message" role="status">{notice}<button className="error-dismiss" onClick={() => setNotice('')} aria-label="Dismiss">×</button></div>}

          <dl className="source-summary">
            <div><dt>Datasets</dt><dd className="source-summary-value">{pack.datasetCount}</dd></div>
            <div><dt>Measures</dt><dd className="source-summary-value">{pack.measureCount}</dd></div>
            <div><dt>Suggested KPIs</dt><dd className="source-summary-value">{pack.kpiCount}</dd></div>
            <div><dt>Ready-made reports</dt><dd className="source-summary-value">{pack.installed ? <Link to="/reports">{pack.reportCount}</Link> : pack.reportCount}</dd></div>
          </dl>

          {result && (
            <section className="source-card pack-result" aria-labelledby="pack-installed-heading">
              <h3 id="pack-installed-heading">{pack.title} installed</h3>
              <p>
                {result.datasets.filter((d) => d.created).length} datasets created
                {result.datasets.some((d) => !d.created) ? `, ${result.datasets.filter((d) => !d.created).length} existing datasets linked` : ''}
                {' '}and {result.kpis.length} KPIs added. The datasets show <em>Never synced</em> until the first sync arrives.
                {pack.nativeConnector && <> Connect {pack.title} below to start syncing.</>}
                {pack.agent && <> Set up the Kinspeed Wallboard Agent below to start syncing.</>}
              </p>
              {result.skippedKpis?.length > 0 && (
                <ul className="pack-list">
                  {result.skippedKpis.map((s) => (
                    <li key={s.key}>
                      {kpis.find((k) => k.key === s.key)?.name || s.key} was not added. {s.reason}
                    </li>
                  ))}
                </ul>
              )}
              {!pack.nativeConnector && !pack.agent && <>
              <h4>Connection details for Kinspeed Connect</h4>
              <p className="source-hint">
                Create a key with the Datasets permission on the <Link to="/admin/api-keys">API keys</Link> page, then enter it and this URL on the {result.connection.connector?.name || 'plugin'}&apos;s connection profile.
              </p>
              {result.connection.wallboardUrl && <CopyField label="Wallboard URL" value={result.connection.wallboardUrl} />}
              <p className="source-hint">
                {result.notified
                  ? 'Kinspeed has been notified and will set up the sync.'
                  : 'Contact Kinspeed to set up the sync.'}
              </p>
              </>}
            </section>
          )}

          {pack.update && isAdmin && (
            <section className="source-card" aria-labelledby="pack-update-heading">
              <h3 id="pack-update-heading">Update available</h3>
              <p className="source-hint" style={{ marginTop: 0 }}>
                Version {pack.update.version} adds more data. Your existing data, settings, reports and any fields you added are kept.
              </p>
              {pack.update.datasets.length > 0 && (
                <>
                  <h4>New datasets</h4>
                  <ul className="pack-list">{pack.update.datasets.map((d) => <li key={d.name}><strong>{d.title}</strong>{d.description ? `: ${d.description}` : ''}</li>)}</ul>
                </>
              )}
              {pack.update.kpis.length > 0 && (
                <>
                  <h4>New KPIs</h4>
                  <div className="connection-checks">
                    {pack.update.kpis.map((k) => (
                      <label key={k.key}>
                        <input type="checkbox" aria-label={`Add ${k.name}`} checked={updateKpis[k.key] !== false} onChange={(e) => setUpdateKpis((s) => ({ ...s, [k.key]: e.target.checked }))} />
                        {k.name}
                      </label>
                    ))}
                  </div>
                </>
              )}
              <p style={{ marginTop: '0.9rem' }}>
                <button type="button" className="btn-primary" onClick={applyUpdate} disabled={busy}>{busy ? 'Updating…' : `Update ${pack.title}`}</button>
              </p>
            </section>
          )}

          {pack.nativeConnector && pack.installed && isAdmin && <ConnectionPanel connectorId={pack.nativeConnector} />}
          {pack.agent && pack.installed && isAdmin && <AgentPanel pack={pack} companyId={companyId} isAdmin={isAdmin} newKey={result?.agentKey || null} />}

          {pack.connector && !pack.nativeConnector && (
            <section className="source-card">
              <h3>How the data arrives</h3>
              <p className="source-hint" style={{ marginTop: 0 }}>
                The {pack.connector.platform || 'Kinspeed Connect'} plugin <strong>{pack.connector.name}</strong> (<code>{pack.connector.plugin}</code>, version {pack.connector.minVersion} or later) reads {pack.title} and pushes to these datasets on a schedule.
                {pack.installed && <> Installed {formatDateTime(pack.installed.installedAt)}{pack.installed.installedBy ? ` by ${pack.installed.installedBy}` : ''}{pack.installed.apiKey ? `; sync key ${pack.installed.apiKey.name} (${pack.installed.apiKey.prefix}…)` : ''}.</>}
              </p>
            </section>
          )}

          <section className="source-card" aria-labelledby="pack-datasets-heading">
            <h3 id="pack-datasets-heading">Datasets</h3>
            {pack.datasets.map((d) => <DatasetDefinition key={d.name} dataset={d} />)}
            <p className="source-hint">You can add your own measures, reports and dashboards on these datasets. The pack&apos;s columns and measures are kept up to date by Kinspeed.</p>
          </section>

          {!pack.installed && (
            <section className="source-card" aria-labelledby="pack-kpis-heading">
              <h3 id="pack-kpis-heading">KPIs</h3>
              <p className="source-hint" style={{ marginTop: 0 }}>Choose the KPIs to add and, optionally, their targets. Targets can be changed later on the KPIs page.</p>
              <table className="datasets-table pack-kpis">
                <thead><tr><th aria-label="Add" /><th>KPI</th><th>Period</th><th>Department</th><th>Target</th></tr></thead>
                <tbody>
                  {kpis.map((k) => (
                    <tr key={k.key}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Add ${k.name}`}
                          checked={Boolean(selected[k.key])}
                          onChange={(e) => setSelected((s) => ({ ...s, [k.key]: e.target.checked }))}
                          disabled={!isAdmin || k.alreadyDefined}
                        />
                      </td>
                      <td>
                        <strong>{k.name}</strong>
                        {k.alreadyDefined && <span className="dataset-badge">Already a KPI</span>}
                        {k.description && <div className="kpi-key">{k.description}</div>}
                        {k.alreadyDefined && <div className="kpi-key">This company already has a KPI with the key <code>{k.key}</code>, so the pack leaves it as it is.</div>}
                      </td>
                      <td>{CADENCE_KPI[k.cadence] || k.cadence}</td>
                      <td>{k.department || '—'}</td>
                      <td>
                        <span className="pack-target">
                          <input
                            type="number"
                            inputMode="decimal"
                            aria-label={`Target for ${k.name}`}
                            value={targets[k.key] ?? ''}
                            onChange={(e) => setTargets((t) => ({ ...t, [k.key]: e.target.value }))}
                            disabled={!isAdmin || !selected[k.key]}
                          />
                          {k.format === 'percent' && <span>%</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {isAdmin
                ? (
                  <div className="pack-install">
                    <button type="button" className="btn-primary" onClick={install} disabled={busy}>{busy ? 'Installing…' : `Install ${pack.title}`}</button>
                    <span className="source-hint">{pack.agent
                      ? 'Creates the datasets and KPIs, and a key for the Kinspeed Wallboard Agent.'
                      : pack.nativeConnector ? 'Creates the datasets and KPIs. You then connect ' + pack.title + ' on this page.' : 'Creates the datasets and KPIs and notifies Kinspeed.'}</span>
                  </div>
                )
                : <p className="source-hint">Ask a company admin to install {pack.title}.</p>}
            </section>
          )}

          {pack.installed && isAdmin && (
            <section className="source-card source-danger" aria-labelledby="pack-uninstall-heading">
              <h3 id="pack-uninstall-heading">Uninstall {pack.title}</h3>
              <p>Uninstalling keeps the datasets, their data and the KPIs unless you choose to delete them.</p>
              <label className="pack-checkbox">
                <input type="checkbox" checked={deleteData} onChange={(e) => setDeleteData(e.target.checked)} />
                Also delete the {pack.datasetCount} datasets, their data and the KPIs measured on them
              </label>
              <button type="button" className="btn-danger" onClick={uninstall} disabled={busy}>{deleteData ? 'Uninstall and delete data' : 'Uninstall'}</button>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default PackPage;
